import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import WhatsAppIcon from '@/components/WhatsAppIcon.jsx';

const ThankYouPage = () => {
  const waMessage = encodeURIComponent('Olá! Acabei de enviar meu pedido no site e gostaria de agilizar meu atendimento.');

  const checkmarkVariants = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: { 
      pathLength: 1, 
      opacity: 1,
      transition: { duration: 0.8, ease: "easeInOut", delay: 0.2 }
    }
  };

  const circleVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: { 
      scale: 1, 
      opacity: 1,
      transition: { duration: 0.6, type: "spring", bounce: 0.4 }
    }
  };

  return (
    <div 
      className="min-h-screen bg-section-dark text-foreground flex flex-col items-center justify-center relative overflow-hidden" 
      data-page="thank_you" 
      data-conversion-status="success"
    >
      <Helmet>
        <title>Pedido Recebido com Sucesso | Hiperpan</title>
        <meta name="robots" content="noindex, nofollow" />
        {/* 
          <!-- GTM E TAGS DE CONVERSÃO RECOMENDADAS PARA SEREM INSERIDAS VIA HEADER -->
          <!-- O ideal é que o GTM dispare quando a URL for /obrigado ou o data-page='thank_you' for lido -->
        */}
      </Helmet>

      <div className="absolute inset-0 bg-grid-pattern opacity-30 z-0 pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/10 blur-[150px] rounded-full pointer-events-none" />

      <div className="container-custom relative z-10 py-16 md:py-20 w-full max-w-3xl">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-background-alt/90 backdrop-blur-md p-8 md:p-14 text-center border border-primary/30 rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.4)]"
        >
          
          <div className="flex justify-center mb-8">
            <motion.svg 
              className="w-20 h-20 md:w-24 md:h-24 overflow-visible" 
              viewBox="0 0 100 100"
              initial="hidden"
              animate="visible"
            >
              <motion.circle 
                cx="50" cy="50" r="45" 
                fill="hsla(142, 71%, 45%, 0.1)" 
                stroke="hsla(142, 71%, 45%, 0.3)" 
                strokeWidth="2"
                variants={circleVariants}
                style={{ filter: 'drop-shadow(0 0 20px rgba(34,197,94,0.3))' }}
              />
              <motion.path 
                d="M30 50 L45 65 L70 35" 
                fill="transparent" 
                stroke="hsl(142, 71%, 45%)" 
                strokeWidth="6" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                variants={checkmarkVariants}
              />
            </motion.svg>
          </div>
          
          <h1 className="heading-2 mb-4 text-white text-balance">
            Pedido Enviado com Sucesso! 🎉
          </h1>

          <p className="subtitle-large text-muted-foreground mb-10 max-w-xl mx-auto">
            Recebemos sua solicitação e nossa equipe comercial vai preparar sua tabela de produtos. <strong className="text-white font-semibold">Em breve entraremos em contato pelo WhatsApp.</strong>
          </p>

          <div className="bg-black/20 border border-white/10 rounded-2xl p-6 md:p-8 mb-10 text-left max-w-xl mx-auto">
            <h3 className="text-lg font-bold mb-5 text-white">Próximos Passos:</h3>
            <ul className="space-y-5 body-text text-muted-foreground">
              <li className="flex items-start gap-4">
                <span className="w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center shrink-0 font-bold border border-primary/30">1</span>
                <span>Conferimos o mix ideal para o seu segmento (padaria, confeitaria, restaurante ou cafeteria).</span>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center shrink-0 font-bold border border-primary/30">2</span>
                <span>Entramos em contato pelo WhatsApp informado com sua tabela comercial personalizada.</span>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center shrink-0 font-bold border border-primary/30">3</span>
                <span>Combinamos sua primeira entrega em até 24h para todo o Rio Grande do Sul.</span>
              </li>
            </ul>
          </div>

          <div className="flex flex-col items-center justify-center gap-5">
            <a
              href={`https://wa.me/5551995420336?text=${waMessage}`}
              target="_blank"
              rel="noopener noreferrer"
              data-gtm="whatsapp"
              data-cta-button="whatsapp_agilizar"
              className="gtm-whatsapp btn-neon h-14 md:h-16 px-8 text-lg font-bold w-full sm:w-auto bg-[#25D366] hover:bg-[#20bd5a] shadow-[0_0_20px_rgba(37,211,102,0.3)] hover:shadow-[0_0_30px_rgba(37,211,102,0.5)] text-white"
            >
              <WhatsAppIcon className="w-6 h-6 mr-3" />
              Falar Agora no WhatsApp
            </a>
            
            <Link 
              to="/"
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-white transition-colors py-2 font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar para a página inicial
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ThankYouPage;